/**
 * Service Map Service
 * Handles service map generation and management
 *
 * @module ServiceMapService
 * Sprint 32: OOA/OOD Refactoring
 */

import {
    ServiceMap,
    ServiceMapGenerationResponse,
    Result
} from '../types/ii-agent-types';
import { CandidParser } from '../parsers/candid-parser';
import { AnalysisHistory } from '../utils/analysis-history';
import { showNotification } from '../utils/notification-utils';

/**
 * Interface for service map service
 */
export interface IServiceMapService {
    generate(prompt: string): Promise<ServiceMap>;
    generateLive(prompt: string): Promise<ServiceMapGenerationResponse>;
}

/**
 * Service for service map generation and management
 */
export class ServiceMapService implements IServiceMapService {
    private client: any;
    private parser: CandidParser;
    private history: AnalysisHistory;

    constructor(client: any, history: AnalysisHistory) {
        this.client = client;
        this.parser = new CandidParser();
        this.history = history;
    }

    /**
     * Generate service map from natural language prompt
     */
    async generate(prompt: string): Promise<ServiceMap> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }

            console.log('Calling generate_service_map with prompt:', prompt);
            showNotification('Generating service map...', 'info');

            const result = await this.client.callCanister(
                'ii_agent',
                'generate_service_map',
                [prompt]
            ) as Result<ServiceMap | string, string>;

            console.log('Raw result from canister:', result);

            if (result && 'Ok' in result) {
                let serviceMap = result.Ok;

                // If result.Ok is a raw Candid string, parse it
                if (typeof serviceMap === 'string') {
                    console.log('Detected raw Candid string, parsing...');
                    serviceMap = this.parser.parseServiceMap(serviceMap);
                }

                console.log('Service map generated successfully:', serviceMap);

                this.history.add({
                    type: 'service_map',
                    timestamp: Date.now(),
                    data: serviceMap
                });

                return serviceMap as ServiceMap;
            } else {
                const error = 'Err' in result ? result.Err : 'Service map generation failed';
                console.error('Service map generation failed:', error);
                throw new Error(error);
            }
        } catch (error) {
            console.error('Error generating service map:', error);
            showNotification('Service map generation failed: ' + (error as Error).message, 'error');
            throw error;
        }
    }

    /**
     * Generate service map with live step-by-step updates
     */
    async generateLive(prompt: string): Promise<ServiceMapGenerationResponse> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }

            console.log('Calling generate_service_map_live with prompt:', prompt);
            showNotification('Starting live service map generation...', 'info');

            const result = await this.client.callCanister(
                'ii_agent',
                'generate_service_map_live',
                [prompt]
            ) as any;

            console.log('Raw result from generate_service_map_live:', result);
            console.log('Result type:', typeof result);
            console.log('Result keys:', Object.keys(result));

            // The proxy returns { data: {...}, raw: "...", success: true }
            // We need to access the data field first
            let actualResult = result;
            if (result && typeof result === 'object' && 'data' in result && result.success) {
                console.log('Using result.data from proxy response');
                actualResult = result.data;
            }

            console.log('actualResult:', actualResult);
            console.log('actualResult type:', typeof actualResult);

            if (actualResult && 'Ok' in actualResult) {
                let response = actualResult.Ok;

                console.log('Response before processing:', response);
                console.log('Response type:', typeof response);

                // If response is still a string (raw Candid), parse it ourselves
                if (typeof response === 'string') {
                    console.log('Response is raw Candid string, parsing it ourselves...');
                    response = this.parser.parseServiceMapGenerationResponse(response);
                    console.log('Parsed successfully:', response);
                }

                // Handle response object
                if (response && typeof response === 'object') {
                    // Ensure generation_steps is an array
                    if (!response.generation_steps || !Array.isArray(response.generation_steps)) {
                        console.error('Invalid response structure - generation_steps is not an array:', response);
                        console.error('Response keys:', Object.keys(response));
                        throw new Error('Invalid response: generation_steps is missing or not an array');
                    }
                }

                console.log('Service map live generation successful:', response);
                console.log('Steps count:', response.generation_steps?.length || 0);

                this.history.add({
                    type: 'service_map',
                    timestamp: Date.now(),
                    data: response.final_service_map
                });

                return response;
            } else {
                const error = 'Err' in result ? result.Err : 'Live service map generation failed';
                console.error('Live service map generation failed:', error);
                throw new Error(error);
            }
        } catch (error) {
            console.error('Error generating live service map:', error);
            showNotification('Live service map generation failed: ' + (error as Error).message, 'error');
            throw error;
        }
    }
}
